import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PirTypesEnumReverse} from '../../../..';
import { PirBoxProductsDetails } from '../../../model/pirbox/pir-box-product-detail';
import { PirBoxProductSummary } from '../../../model/pirbox/pirbox-product-summary';
import {
  getPirProductGraphDetails,
  retrievePirProductDetail,
  retrievePirProductSummary
} from './pir-box-thunks';
import { PirBoxState } from './pir-box-types';

const initialState: PirBoxState = {
  status: 'idle',
  error: '',
  positions: new Map<string, PirBoxProductsDetails[]>(),
  productsSummary: [] as PirBoxProductSummary[],
  hasAlternative: false,
  hasOrdinary: false,
  selectedMandateCode: undefined,
  graphDetails: undefined,
  summaryInFlight: false,
  detailsInFlight: [],
};

const removeDetailInFlight = (state: PirBoxState, pirCode: string) => {
  state.detailsInFlight = (state.detailsInFlight ?? []).filter(
    (code) => code !== pirCode
  );
};

const pirboxSlice = createSlice({
  name: 'features/pirbox',
  initialState: initialState,
  reducers: {
    clearError(state) {
      state.status = 'idle';
    },
    setMandate(state, action: PayloadAction<string>) {
      state.selectedMandateCode = action.payload;
    },
    resetPirBox: () => initialState,
    resetProductDetails(state) {
      state.selectedMandateCode = undefined;
      state.graphDetails = undefined;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(retrievePirProductSummary.fulfilled, (state, action) => {
      state.status = 'succeeded'
      state.summaryInFlight = false;
      state.productsSummary=action.payload;
      action.payload?.forEach((value)=>{
        if (value.pirType === PirTypesEnumReverse.UCITS) {
          state.hasOrdinary = true;
        }else if (value.pirType === PirTypesEnumReverse.FIA){
          state.hasAlternative = true;
        }
      })
    });
    builder.addCase(retrievePirProductSummary.pending, (state) => {
      state.status = 'pending'
      state.summaryInFlight = true;
    });
    builder.addCase(retrievePirProductSummary.rejected, (state,action) => {
      state.status = 'failed';
      state.summaryInFlight = false;
      state.error = action.payload || 'GENERIC_ERROR';
    });
    builder.addCase(retrievePirProductDetail.fulfilled, (state, action) => {
      state.status = 'succeeded'
      removeDetailInFlight(state, action.meta.arg);
      state.positions.set(action.payload.pirCode, action.payload.positions);
    });
    builder.addCase(retrievePirProductDetail.pending, (state, action) => {
      state.status = 'pending'
      state.detailsInFlight = [...(state.detailsInFlight ?? []), action.meta.arg];
    });
    builder.addCase(retrievePirProductDetail.rejected, (state,action) => {
      state.status = 'failed';
      removeDetailInFlight(state, action.meta.arg);
      state.error = action.payload || 'GENERIC_ERROR';
    });
    builder.addCase(getPirProductGraphDetails.pending, (state) => {
      state.status = 'loading';
      state.graphDetails = undefined;
    });
    builder.addCase(getPirProductGraphDetails.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload || 'GENERIC_ERROR';
      state.graphDetails = undefined;
    });
    builder.addCase(getPirProductGraphDetails.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.graphDetails = action.payload;
    });
  },
});

export const pirboxActions = pirboxSlice.actions;
export default pirboxSlice.reducer;
